import React, { forwardRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { isString } from 'lodash';
import cn from 'classnames';
import ErrorMessage from 'shared/components/ErrorMessage';
import Hint from 'shared/components/Hint';
import Label from 'shared/components/Form/Label';
import styles from './Input.module.scss';

const ChildrenWrapper = ({ children }) => <div className={styles.childWrap}>{children}</div>;

const Input = forwardRef(({
  label,
  labelSuffix,
  labelHint,
  labelHintIconClassName,
  value: rawValue,
  iconClass,
  error,
  multiline,
  rows,
  inline,
  noMargin,
  className,
  fullWidth,
  children,
  hint,
  ...props
}, ref) => {
  const value = rawValue !== null ? rawValue : '';
  const id = props.id || `${props.name || 'input'}-${Math.round(Math.random() * 10000)}`;
  const placeholder = props.placeholder || label;
  const hasError = !!error;
  const Wrapper = children ? ChildrenWrapper : Fragment;

  const invalidLength = props.maxLength ? value.toString().length > ~~props.maxLength : false;

  return (
    <>
      {label !== null && (
        <div className={styles.labelWrapper}>
          <Label htmlFor={id} hasError={hasError} inline={inline} suffix={labelSuffix}>
            {label}
            {labelHint && (
              <Hint iconClassName={labelHintIconClassName} tooltip={labelHint} />
            )}
          </Label>
          {props.maxLength && (
            <span className={invalidLength ? styles.invalidLength : null}>
              {value.toString().length}/{props.maxLength}
            </span>
          )}
        </div>
      )}
      <Wrapper>
        <div
          className={cn(
            styles.input,
            {
              [styles.error]: hasError,
              error: hasError,
              [styles.inline]: inline,
              [styles.noMargin]: noMargin,
              [styles.fullWidth]: fullWidth
            },
            className
          )}
        >
          {multiline ? (
            <textarea
              ref={ref}
              id={id}
              value={value}
              rows={rows}
              placeholder={placeholder}
              {...props}
              data-hj-allow="true"
            />
          ) : (
            <input
              ref={ref}
              id={id}
              value={value}
              placeholder={placeholder}
              type="text"
              {...props}
              data-hj-allow="true"
            />
          )}
          {iconClass && <i className={iconClass} />}
        </div>
        {children}
      </Wrapper>
      {hint}
      {isString(error) && <ErrorMessage>{error}</ErrorMessage>}
    </>
  );
});

// eslint-disable-next-line
export const propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  labelSuffix: PropTypes.string,
  labelHint: PropTypes.string,
  labelHintIconClassName: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  iconClass: PropTypes.string,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  className: PropTypes.string,
  inline: PropTypes.bool,
  noMargin: PropTypes.bool,
  fullWidth: PropTypes.bool,
};

// eslint-disable-next-line
export const defaultProps = {
  label: null,
  labelSuffix: null,
  labelHint: null,
  labelHintIconClassName: 'la la-question-circle',
  value: '',
  iconClass: null,
  multiline: false,
  rows: 4,
  error: null,
  className: null,
  inline: false,
  noMargin: false,
  fullWidth: false,
};

Input.propTypes = propTypes;
Input.defaultProps = defaultProps;
Input.displayName = 'Input';

export default Input;
